import { Box } from '@mui/material';
import { useGetPoolOptions } from 'components/admin-portal/Tables/Settings/PlanUnits/components/utils/useGetPoolOptions';
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import { useGetClusters } from 'queries';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useGetRequestReason from '../hooks/useGetRequestReason';
import { useRosterFunctions, useSelector, useDispatch } from 'hooks';
import { Filters } from '../types';
import ShowMoreFilters from './ShowMoreFilters';
import SearchButton from './SearchButton';
import FilterButton from './FilterButton';
import StartAndEndDate from './StartAndEndDate';
import { setUserPreference } from 'redux/actionCreators';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import { PlanUnit } from 'components/types';
import { styled } from '@mui/material/styles';
import { yupResolver } from '@hookform/resolvers/yup';
import { filterSchema } from '../schema';
import useGetEmployeeList from '../hooks/useGetEmployeeList';
import { StyledFilterBarBox } from './FilterBar.styles';

const defaultValues = {
  costCenter: null,
  startDate: dayjs(new Date())
    .subtract(1, 'month')
    .startOf('day')
    .toISOString(),
  endDate: dayjs(new Date()).endOf('day').toISOString(),
  cluster: null,
  pool: null,
  employee: null,
  requestReason: null,
  rosterFunction: null
};

const StyledBox = styled(Box)({
  flexGrow: 1,
  marginTop: '8px',
  marginBottom: '8px',
  minWidth: 200,
  maxWidth: 400
});

interface Props {
  onSearch: () => void;
  onFiltersChange: (filters: Filters) => void;
  hasPoolsOrgGrant: boolean;
  hasClustersOrgGrant: boolean;
}

const FilterBar = ({
  onFiltersChange,
  onSearch,
  hasPoolsOrgGrant,
  hasClustersOrgGrant
}: Props) => {
  const { t } = useTranslation('common');
  const { control, watch, setValue, formState } = useForm<Filters>({
    resolver: yupResolver(filterSchema),
    mode: 'onChange',
    defaultValues: {
      costCenter: null,
      startDate: dayjs(new Date())
        .subtract(1, 'month')
        .startOf('day')
        .toISOString(), // Default to one month ago
      endDate: dayjs(new Date()).endOf('day').toISOString(), // Default to today
      cluster: null,
      pool: null,
      employee: null,
      requestReason: null,
      rosterFunction: null
    }
  });

  const filterHasErrors: boolean = Object.keys(formState.errors).length > 0;

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const { data: clusters } = hasClustersOrgGrant
    ? useGetClusters()
    : { data: [] };
  const { data: pools } = hasPoolsOrgGrant ? useGetPoolOptions() : { data: [] };

  const { data: employeeListData } = useGetEmployeeList();

  // order employeeList by nameInformal
  const employeeList = employeeListData?.sort((a, b) =>
    a.nameInformal.localeCompare(b.nameInformal, 'en', {
      sensitivity: 'base'
    })
  );

  const { data: requestReasonList } = useGetRequestReason();
  const { rosterFunctions: rosterFunctionOptions } = useRosterFunctions();

  const dispatch = useDispatch();
  const { preferences } = useSelector((state) => state);

  const flexLastUsedCostCenterSelection =
    preferences.flexLastUsedCostCenterSelection;
  const flexShouldSaveCostCenterSelection =
    preferences.flexShouldSaveCostCenterSelection;
  const [costCenterList, setCostCenterList] = useState<PlanUnit[]>([]);

  const saveSelection = (value: (string | number)[]) => {
    dispatch(
      setUserPreference({
        key: 'flexLastUsedCostCenterSelection',
        value
      })
    );
  };

  const formData = watch();

  const {
    costCenter,
    startDate,
    endDate,
    cluster,
    pool,
    employee,
    reason,
    rosterFunction
  } = formData;

  const updateFilters = useCallback(() => {
    const selectedCostCenterIds = costCenterList?.map((el) => el.uniqId) || [];
    const selectedEmployee = employeeList?.find(
      (el) => el.empId === employee?.empId
    );
    const selectedPool = pools?.find((el) => el.value === pool?.value);
    const selectedCluster = clusters?.find(
      (el) => el.clusterId === cluster?.clusterId
    );
    const selectedRequestReason = requestReasonList?.find(
      (el) => el.name === reason?.name
    );
    const selectedRosterFunction = rosterFunctionOptions?.find(
      (el) => el.id === rosterFunction?.id
    );

    const filters: Filters = {
      costCenterId: selectedCostCenterIds as number[],
      startDate: dayjs(startDate, 'DD-MM-YYYY').isValid()
        ? dayjs(startDate, 'DD-MM-YYYY').startOf('day').toISOString()
        : null,
      endDate: dayjs(endDate, 'DD-MM-YYYY').isValid()
        ? dayjs(endDate, 'DD-MM-YYYY').endOf('day').toISOString()
        : null,
      clusterId:
        selectedCluster && 'clusterId' in selectedCluster
          ? selectedCluster.clusterId
          : null,
      poolId:
        selectedPool && 'value' in selectedPool ? selectedPool.value : null,
      employeeId:
        selectedEmployee && 'empId' in selectedEmployee
          ? selectedEmployee?.empId
          : null,
      requestReason:
        selectedRequestReason && 'name' in selectedRequestReason
          ? selectedRequestReason?.name
          : null,
      functionId:
        selectedRosterFunction && 'id' in selectedRosterFunction
          ? selectedRosterFunction?.id
          : null
    };

    onFiltersChange(filters);
  }, [
    costCenter,
    startDate,
    endDate,
    cluster,
    pool,
    employee,
    costCenterList,
    reason,
    rosterFunction,
    onFiltersChange
  ]);

  const handleMoreFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  useEffect(() => {
    updateFilters();
  }, [
    costCenterList,
    startDate,
    endDate,
    cluster,
    pool,
    employee,
    reason,
    rosterFunction
  ]);

  const onSelectedCostCenter = (value: PlanUnit[]) => {
    setCostCenterList(value);
    flexShouldSaveCostCenterSelection &&
      saveSelection(value.map(({ id }) => id));
  };

  const defaultSelectedCostCenter =
    flexLastUsedCostCenterSelection?.map(
      (uniqId) =>
        ({
          uniqId
        }) as PlanUnit
    ) || undefined;

  useEffect(() => {
    setValue('startDate', dayjs(new Date()).startOf('month').toISOString());
    setValue('endDate', dayjs(new Date()).endOf('month').toISOString());
  }, []);

  return (
    <>
      <StyledFilterBarBox>
        <Box
          display="flex"
          flexWrap="wrap"
          rowGap={0}
          columnGap={2}
          justifyContent="start"
          alignItems="center"
          width="100%"
          maxWidth={1536}
          marginRight="auto"
        >
          <Box flexGrow={0} maxWidth={400} marginBottom={2} marginRight={2}>
            <PlanUnitSelector
              hideGroups
              doNotPersist
              onSelected={onSelectedCostCenter}
              useEmployeePlanunits
              onClear={() => onSelectedCostCenter([])}
              defaultValue={defaultSelectedCostCenter}
            />
          </Box>
          <StartAndEndDate {...{ control, defaultValues }} />
          {hasClustersOrgGrant && (
            <StyledBox>
              <ControlledAutoComplete
                disableSorting
                optionLabelProp="name"
                idProperty="clusterId"
                variant="outlined"
                control={control}
                name="cluster"
                label={t('cluster')}
                options={clusters ?? []}
                fullWidth
              />
            </StyledBox>
          )}
          {hasPoolsOrgGrant && (
            <StyledBox>
              <ControlledAutoComplete
                disableSorting
                optionLabelProp="label"
                idProperty="value"
                variant="outlined"
                control={control}
                name="pool"
                label={t('Pools')}
                options={pools ?? []}
                fullWidth
              />
            </StyledBox>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            marginTop: (theme) => theme.spacing(4),
            gap: (theme) => theme.spacing(2)
          }}
        >
          <FilterButton {...{ handleMoreFilters, showMoreFilters }} />
          <SearchButton {...{ onSearch }} disabled={filterHasErrors} />
        </Box>
      </StyledFilterBarBox>
      {showMoreFilters && (
        <ShowMoreFilters
          {...{
            control,
            employeeList,
            requestReasonList,
            rosterFunctionOptions
          }}
        />
      )}
    </>
  );
};

export default FilterBar;
